/**
 * Build CSV file content from in-memory rows (client export).
 * For Media Devoted | Created By Ciro Autiero
 */

import { triggerBlobFileDownload } from '@/lib/triggerBlobFileDownload';

import type { GenericStatsRow } from '../shared/generic-stats.types';
import type { CsvColumnSpec } from './buildCsvColumns';
import { sanitizeCsvDownloadBaseName } from './sanitizeCsvDownloadBaseName';

function escCell(s: string): string {
  if (!s.includes(',') && !s.includes('"') && !s.includes('\n')) return s;
  return `"${String(s).replace(/"/g, '""')}"`;
}

export interface BuildClientCsvPayloadOptions {
  downloadBaseName?: string;
}

export function buildClientCsvPayload(
  tab: string,
  columns: CsvColumnSpec[],
  rows: GenericStatsRow[],
  orderedKeys: string[],
  options?: BuildClientCsvPayloadOptions,
): { csv: string; filename: string } {
  const colSpecs = orderedKeys
    .map((k) => columns.find((c) => c.key === k))
    .filter(
      (c): c is CsvColumnSpec =>
        c != null && c.key !== 'crossSourceFilterNote',
    );
  const headerRow = colSpecs.map((c) => escCell(c.label)).join(',');
  const dataRows = rows.map((row) =>
    colSpecs.map((col) => escCell(String(col.getValue(row) ?? ''))).join(','),
  );
  const csv = [headerRow, ...dataRows].join('\n');
  const day = new Date().toISOString().slice(0, 10);
  const base = sanitizeCsvDownloadBaseName(options?.downloadBaseName);
  const filename = base ? `${base}.csv` : `${tab}-export-${day}.csv`;
  return { csv, filename };
}

export function downloadClientCsvFile(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  triggerBlobFileDownload(blob, filename);
}
